import React, { useState, useEffect } from 'react';
import { 
  User, MessageSquare, FileText, Mail, CheckCircle, 
  PlusCircle, Send, Edit3, Calendar, DollarSign, Users 
} from 'lucide-react';
import { authAPI, customerAPI } from '../services/api';

const TradePortal = () => {
  const [currentView, setCurrentView] = useState('landing');
  const [currentUser, setCurrentUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for existing session and reset token
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    
    if (resetToken) {
      setCurrentView('reset-password');
      return;
    }

    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('userData');
    
    if (token && userData) {
      setCurrentUser(JSON.parse(userData));
      setCurrentView('dashboard');
    }
  }, []);

  // Fetch data when entering dashboard
  useEffect(() => {
    if (currentView === 'dashboard') {
      fetchDashboardData();
    }
  }, [currentView]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [enquiriesRes, quotesRes] = await Promise.all([
        customerAPI.getEnquiries(),
        customerAPI.getQuotes()
      ]);
      setEnquiries(enquiriesRes.data);
      setQuotes(quotesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const LandingPage = () => {
    const [showSignUp, setShowSignUp] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [formData, setFormData] = useState({
      businessName: '',
      contactName: '',
      email: '',
      phone: '',
      password: ''
    });
    const [resetEmail, setResetEmail] = useState('');
    const [formError, setFormError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSignUp = async () => {
      setFormError('');
      setIsSubmitting(true);

      try {
        if (showSignUp) {
          // Register
          if (!formData.businessName || !formData.contactName || !formData.phone) {
            setFormError('Please fill in all fields');
            setIsSubmitting(false);
            return;
          }
          
          const response = await authAPI.register(formData);
          if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('userData', JSON.stringify(response.data.user));
            setCurrentUser(response.data.user);
            setCurrentView('dashboard');
          }
        } else {
          // Login
          if (!formData.email || !formData.password) {
            setFormError('Please enter email and password');
            setIsSubmitting(false);
            return;
          }
          
          const response = await authAPI.login({
            email: formData.email,
            password: formData.password
          });
          
          if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('userData', JSON.stringify(response.data.user));
            setCurrentUser(response.data.user);
            setCurrentView('dashboard');
          }
        }
      } catch (error) {
        console.error('Auth error:', error);
        setFormError(
          error.response?.data?.error || 
          (showSignUp ? 'Registration failed. Email may already be in use.' : 'Invalid email or password')
        );
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleForgotPassword = async () => {
      setFormError('');
      setIsSubmitting(true);

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: resetEmail })
        });

        if (response.ok) {
          alert('Reset link sent to your email if account exists');
          setShowForgotPassword(false);
          setResetEmail('');
        }
      } catch (error) {
        setFormError('Failed to send reset email');
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        {/* Header */}
        <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b border-green-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <img 
                  src="/logo.png" 
                  alt="Newgate Nurseries Logo" 
                  className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center" style={{display: 'none'}}>
                  <span className="text-white font-bold text-lg sm:text-xl">N</span>
                </div>
                <div>
                  <h1 className="text-lg sm:text-xl font-bold text-gray-900">Newgate Nurseries</h1>
                  <p className="text-xs sm:text-sm text-green-600">Trade Portal</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowSignUp(!showSignUp);
                  setFormError('');
                }}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 sm:px-6 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl text-sm sm:text-base"
              >
                {showSignUp ? 'Sign In' : 'Get Started'}
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Hero Content - Order 1 on mobile */}
            <div className="space-y-6 lg:space-y-8 order-1">
              <div className="space-y-4">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  Premium Plants for
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">
                    Trade Professionals
                  </span>
                </h2>
                <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
                  Streamline your plant sourcing with our professional trade portal. 
                  Make enquiries, receive instant quotes, and manage orders seamlessly.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
                <div className="bg-white/60 backdrop-blur-sm p-4 lg:p-6 rounded-2xl border border-green-100">
                  <MessageSquare className="w-6 h-6 lg:w-8 lg:h-8 text-green-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">Smart Enquiries</h3>
                  <p className="text-gray-600 text-sm">Submit detailed plant requests and get expert responses</p>
                </div>
                <div className="bg-white/60 backdrop-blur-sm p-4 lg:p-6 rounded-2xl border border-green-100">
                  <FileText className="w-6 h-6 lg:w-8 lg:h-8 text-green-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">Digital Quotes</h3>
                  <p className="text-gray-600 text-sm">Receive and sign quotes electronically</p>
                </div>
                <div className="bg-white/60 backdrop-blur-sm p-4 lg:p-6 rounded-2xl border border-green-100">
                  <Mail className="w-6 h-6 lg:w-8 lg:h-8 text-green-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">Auto Invoicing</h3>
                  <p className="text-gray-600 text-sm">Automated email invoicing for signed quotes</p>
                </div>
                <div className="bg-white/60 backdrop-blur-sm p-4 lg:p-6 rounded-2xl border border-green-100">
                  <Users className="w-6 h-6 lg:w-8 lg:h-8 text-green-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">Trade Account</h3>
                  <p className="text-gray-600 text-sm">Exclusive pricing and priority service</p>
                </div>
              </div>
            </div>

            {/* Sign Up Form - Order 2 on mobile */}
            <div className="bg-white/80 backdrop-blur-md p-6 lg:p-8 rounded-3xl shadow-xl border border-green-100 order-2">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {showSignUp ? 'Create Trade Account' : 'Welcome Back'}
                </h3>
                <p className="text-gray-600">
                  {showSignUp ? 'Join our network of trade professionals' : 'Sign in to your trade account'}
                </p>
              </div>

              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {formError}
                </div>
              )}

              <div className="space-y-6">
                {showSignUp && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
                      <input
                        type="text"
                        required
                        value={formData.businessName}
                        onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        placeholder="Your Business Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name</label>
                      <input
                        type="text"
                        required
                        value={formData.contactName}
                        onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        placeholder="Your Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        placeholder="Your Phone Number"
                      />
                    </div>
                  </>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    placeholder="your@business.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    placeholder="••••••••"
                  />
                </div>

                <button
                  onClick={handleSignUp}
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Please wait...' : (showSignUp ? 'Create Account' : 'Sign In')}
                </button>
              </div>

              <p className="text-center text-sm text-gray-600 mt-6">
                {showSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button
                  onClick={() => {
                    setShowSignUp(!showSignUp);
                    setFormError('');
                  }}
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  {showSignUp ? 'Sign in' : 'Sign up'}
                </button>
              </p>
              
              {!showSignUp && (
                <p className="text-center text-sm text-gray-600 mt-2">
                  <button
                    onClick={() => setShowForgotPassword(true)}
                    className="text-green-600 hover:text-green-700 font-medium"
                  >
                    Forgot password?
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold mb-4">Reset Password</h3>
              
              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {formError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500"
                    placeholder="your@business.com"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleForgotPassword}
                    disabled={isSubmitting}
                    className="flex-1 bg-green-600 text-white py-3 rounded-xl hover:bg-green-700 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                  </button>
                  <button
                    onClick={() => setShowForgotPassword(false)}
                    className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="bg-white/80 backdrop-blur-md border-t border-green-100 mt-12">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="text-center text-sm text-gray-600">
              <p>© 2025 Newgate Nurseries Ltd. All rights reserved.</p>
              <p className="mt-1">Software developed by Ben Cookson on behalf of Newgate Nurseries</p>
            </div>
          </div>
        </footer>
      </div>
    );
  };

  const ResetPasswordPage = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleReset = async () => {
      if (newPassword !== confirmPassword) {
        setMessage('Passwords do not match');
        return;
      }

      setIsSubmitting(true);
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, newPassword })
        });

        if (response.ok) {
          setMessage('Password reset successful! You can now login.');
          setTimeout(() => setCurrentView('landing'), 3000);
        } else {
          setMessage('Invalid or expired token');
        }
      } catch (error) {
        setMessage('Reset failed. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
          <h2 className="text-2xl font-bold text-center mb-6">Reset Password</h2>
          
          {message && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              message.includes('successful') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {message}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <button
              onClick={handleReset}
              disabled={isSubmitting}
              className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const Dashboard = () => {
    const [activeTab, setActiveTab] = useState('enquiries');
    const [newEnquiry, setNewEnquiry] = useState({ 
      title: '', 
      description: '', 
      deliveryRequired: false, 
      deliveryAddress: '' 
    });
    const [showNewEnquiry, setShowNewEnquiry] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleNewEnquiry = async () => {
      if (!newEnquiry.title || !newEnquiry.description) {
        alert('Please fill in all required fields');
        return;
      }

      if (newEnquiry.deliveryRequired && !newEnquiry.deliveryAddress) {
        alert('Please provide delivery address');
        return;
      }

      setIsSubmitting(true);
      try {
        const response = await customerAPI.createEnquiry(newEnquiry);
        setEnquiries([response.data, ...enquiries]);
        setNewEnquiry({ title: '', description: '', deliveryRequired: false, deliveryAddress: '' });
        setShowNewEnquiry(false);
        alert('Enquiry submitted successfully! You will receive an SMS confirmation.');
      } catch (error) {
        console.error('Error creating enquiry:', error);
        alert('Failed to submit enquiry. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    };

    const signQuote = async (quoteId) => {
      if (!window.confirm('Are you sure you want to sign this quote? An invoice will be sent to your email.')) {
        return;
      }

      try {
        await customerAPI.signQuote(quoteId);
        await fetchDashboardData(); // Refresh data
        alert('Quote signed successfully! Invoice has been sent to your email.');
      } catch (error) {
        console.error('Error signing quote:', error);
        alert('Failed to sign quote. Please try again.');
      }
    };

    const handleLogout = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      setCurrentUser(null);
      setCurrentView('landing');
    };

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-green-600 to-emerald-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">N</span>
                </div>
                <h1 className="text-lg font-semibold text-gray-900">Newgate Nurseries</h1>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">Welcome, {currentUser?.contactName}</span>
                <button
                  onClick={handleLogout}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto p-4 lg:p-6">
          {loading && (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <p className="mt-2 text-gray-600">Loading...</p>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6 mb-6 lg:mb-8">
            <div className="bg-white p-4 lg:p-6 rounded-xl shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-gray-600">Active Enquiries</p>
                  <p className="text-lg lg:text-2xl font-bold text-gray-900">
                    {enquiries.filter(e => e.status === 'pending').length}
                  </p>
                </div>
                <MessageSquare className="w-6 h-6 lg:w-8 lg:h-8 text-blue-500" />
              </div>
            </div>
            <div className="bg-white p-4 lg:p-6 rounded-xl shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-gray-600">Pending Quotes</p>
                  <p className="text-lg lg:text-2xl font-bold text-gray-900">
                    {quotes.filter(q => q.status === 'pending_signature').length}
                  </p>
                </div>
                <FileText className="w-6 h-6 lg:w-8 lg:h-8 text-green-500" />
              </div>
            </div>
            <div className="bg-white p-4 lg:p-6 rounded-xl shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-gray-600">Signed Quotes</p>
                  <p className="text-lg lg:text-2xl font-bold text-gray-900">
                    {quotes.filter(q => q.status === 'signed').length}
                  </p>
                </div>
                <CheckCircle className="w-6 h-6 lg:w-8 lg:h-8 text-emerald-500" />
              </div>
            </div>
            <div className="bg-white p-4 lg:p-6 rounded-xl shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-gray-600">Total Value</p>
                  <p className="text-lg lg:text-2xl font-bold text-gray-900">
                    £{quotes.reduce((sum, q) => sum + parseFloat(q.total || 0), 0).toLocaleString()}
                  </p>
                </div>
                <DollarSign className="w-6 h-6 lg:w-8 lg:h-8 text-purple-500" />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-sm border">
            <div className="border-b border-gray-200 overflow-x-auto">
              <div className="flex space-x-4 lg:space-x-8 px-4 lg:px-6 min-w-max">
                <button
                  onClick={() => setActiveTab('enquiries')}
                  className={`py-3 lg:py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'enquiries'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  My Enquiries
                </button>
                <button
                  onClick={() => setActiveTab('quotes')}
                  className={`py-3 lg:py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'quotes'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Quotes & Invoices
                </button>
              </div>
            </div>

            <div className="p-4 lg:p-6">
              {activeTab === 'enquiries' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">Your Enquiries</h3>
                    <button
                      onClick={() => setShowNewEnquiry(true)}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
                    >
                      <PlusCircle className="w-4 h-4" />
                      <span>New Enquiry</span>
                    </button>
                  </div>

                  {showNewEnquiry && (
                    <div className="bg-gray-50 p-4 lg:p-6 rounded-lg space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Enquiry Title</label>
                        <input
                          type="text"
                          required
                          value={newEnquiry.title}
                          onChange={(e) => setNewEnquiry({...newEnquiry, title: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="e.g., Bulk order for spring bedding plants"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          required
                          value={newEnquiry.description}
                          onChange={(e) => setNewEnquiry({...newEnquiry, description: e.target.value})}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Please provide details about the plants you need, quantities, delivery dates, etc."
                        />
                      </div>
                      
                      <div>
                        <label className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={newEnquiry.deliveryRequired}
                            onChange={(e) => setNewEnquiry({...newEnquiry, deliveryRequired: e.target.checked})}
                            className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Delivery Required</span>
                        </label>
                      </div>

                      {newEnquiry.deliveryRequired && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Address</label>
                          <textarea
                            required
                            value={newEnquiry.deliveryAddress}
                            onChange={(e) => setNewEnquiry({...newEnquiry, deliveryAddress: e.target.value})}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder="Enter full delivery address including postcode"
                          />
                        </div>
                      )}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={handleNewEnquiry}
                          disabled={isSubmitting}
                          className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2 disabled:opacity-50"
                        >
                          <Send className="w-4 h-4" />
                          <span>{isSubmitting ? 'Submitting...' : 'Submit Enquiry'}</span>
                        </button>
                        <button
                          onClick={() => setShowNewEnquiry(false)}
                          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {enquiries.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No enquiries yet. Create your first enquiry!</p>
                    ) : (
                      enquiries.map((enquiry) => (
                        <div key={enquiry.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 gap-2">
                            <div>
                              <h4 className="font-semibold text-gray-900">{enquiry.title}</h4>
                              <p className="text-sm text-gray-600 flex items-center space-x-2">
                                <Calendar className="w-4 h-4" />
                                <span>{new Date(enquiry.created_at).toLocaleDateString()}</span>
                              </p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                              enquiry.status === 'pending' 
                                ? 'bg-yellow-100 text-yellow-800'
                                : enquiry.status === 'quoted'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {enquiry.status === 'pending' ? 'Pending Response' : 
                               enquiry.status === 'quoted' ? 'Quote Provided' : 'Responded'}
                            </span>
                          </div>
                          <p className="text-gray-700 mb-3">{enquiry.description}</p>
                          
                          {enquiry.delivery_required && (
                            <div className="bg-blue-50 p-3 rounded border-l-4 border-blue-400 mb-3">
                              <p className="text-sm font-medium text-blue-800">🚚 Delivery Required</p>
                              <p className="text-blue-700">{enquiry.delivery_address}</p>
                            </div>
                          )}
                          
                          {enquiry.response && (
                            <div className="bg-green-50 p-3 rounded border-l-4 border-green-400">
                              <p className="text-sm font-medium text-green-800">Response from Newgate Nurseries:</p>
                              <p className="text-green-700">{enquiry.response}</p>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'quotes' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">Quotes & Invoices</h3>
                  
                  <div className="space-y-4">
                    {quotes.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No quotes yet. Quotes will appear here once created by our team.</p>
                    ) : (
                      quotes.map((quote) => (
                        <div key={quote.id} className="border border-gray-200 rounded-lg p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h4 className="font-semibold text-gray-900">{quote.title}</h4>
                              <p className="text-sm text-gray-600">
                                Quote Date: {new Date(quote.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              quote.status === 'pending_signature'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {quote.status === 'pending_signature' ? 'Awaiting Signature' : 'Signed & Invoiced'}
                            </span>
                          </div>

                          <div className="bg-gray-50 rounded-lg p-4 mb-4 overflow-x-auto">
                            <table className="w-full text-sm min-w-[400px]">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-2">Item</th>
                                  <th className="text-center py-2">Qty</th>
                                  <th className="text-right py-2">Unit Price</th>
                                  <th className="text-right py-2">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {quote.items?.map((item, index) => (
                                  <tr key={index} className="border-b border-gray-100">
                                    <td className="py-2">{item.item_name}</td>
                                    <td className="text-center py-2">{item.quantity}</td>
                                    <td className="text-right py-2">£{parseFloat(item.unit_price).toFixed(2)}</td>
                                    <td className="text-right py-2">£{parseFloat(item.total).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div className="mt-4 space-y-1 text-right">
                              <div className="flex justify-between">
                                <span>Subtotal:</span>
                                <span>£{parseFloat(quote.subtotal).toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>VAT (20%):</span>
                                <span>£{parseFloat(quote.vat).toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between font-bold text-lg border-t pt-2">
                                <span>Total:</span>
                                <span>£{parseFloat(quote.total).toFixed(2)}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-3">
                            {quote.status === 'pending_signature' ? (
                              <button
                                onClick={() => signQuote(quote.id)}
                                className="flex-1 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2"
                              >
                                <Edit3 className="w-4 h-4" />
                                <span>Sign Quote</span>
                              </button>
                            ) : (
                              <div className="flex items-center space-x-2 text-green-600">
                                <CheckCircle className="w-5 h-5" />
                                <span className="font-medium">
                                  Signed on {new Date(quote.signed_date).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 mt-12">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="text-center text-sm text-gray-600">
              <p>© 2025 Newgate Nurseries Ltd. All rights reserved.</p>
              <p className="mt-1">Software developed by Ben Cookson on behalf of Newgate Nurseries</p>
            </div>
          </div>
        </footer>
      </div>
    );
  };

  return (
    <div>
      {currentView === 'landing' ? <LandingPage /> : 
       currentView === 'reset-password' ? <ResetPasswordPage /> : 
       <Dashboard />}
    </div>
  );
};

export default TradePortal;